/*
 * GROUP CHAT IN A SMALL WINDOW (Amy's feedback FB-0002, 04/11: "thêm tính năng thu nhỏ đoạn chat … dễ để user vừa check cái khác vừa
 * gửi được tin nhắn" — Max: "kiểu popup nhỏ như vầy", Google Chat's). A room is popped out from its head in Group Chat; the window
 * docks bottom-right on every page, folds to its title bar (with the unread count) and comes back, and "open" takes it to the full
 * room. Up to three at once; which rooms are open stays in this browser (localStorage) across pages and reloads. Not drawn on the
 * Group Chat page itself (the room is there) nor on a phone (the full room is the phone's way, #1172).
 * The same routes as the full room: messages (polled every 3 s while open, 8 s folded), send, mark read.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Minus, Maximize2, Send, Plus, Smile, Paperclip } from 'lucide-react';
import i18n from './i18n';
import UserAvatar from './UserAvatar';
import { Mascot } from './AskBubble';
import useAuth from './useAuth';
import { enterSends } from './touchInput';
import { BACKEND_URL } from './backendConfig';
import './GccPopups.css';

const API = `${BACKEND_URL}/api/gcc`;
const KEY = 'gccPopups';
const MAX = 3;
const EMOJI = ['👍', '❤️', '😂', '😊', '😮', '😢', '🙏', '👏', '🎉', '✅', '🔥', '👀', '🤔', '😅', '💯', '📌'];
const call = async (method, url, body) => {
  const r = await fetch(`${API}${url}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : {}, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};
const readLang = () => { const l = String(i18n.language || '').slice(0, 2); return ['ko', 'vi', 'zh', 'fr', 'ja'].includes(l) ? l : 'en'; };
const load = () => { try { const v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v.filter((x) => Number(x.pk)).slice(0, MAX) : []; } catch (e) { return []; } };
const save = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* private window */ } };
const hhmm = (v) => { const d = new Date(String(v).endsWith('Z') || String(v).includes('+') ? v : `${v}Z`); return Number.isNaN(d.getTime()) ? '' : new Date(d.getTime() + 7 * 3600000).toISOString().slice(11, 16); };
// the marks GCC writes (**bold**, _italic_ …) taken out — the small window shows the words
const plain = (x) => String(x || '').replace(/\*\*|__|~~|`/g, '').replace(/(^|\s)_([^_\n]+)_(?=\s|$)/g, '$1$2');

/** Opens a room in a small window — the full room's "pop out" calls this. */
export const popOutRoom = (pk) => window.dispatchEvent(new CustomEvent('gcc-popup', { detail: { pk: Number(pk) } }));

function Popup({ pk, min, index, onFold, onClose, me }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const lastPk = useRef(0);
  const readPk = useRef(0);
  const listRef = useRef(null);
  const pull = useCallback(async (fresh) => {
    const r = await call('GET', `/rooms/${pk}/messages?after=${fresh ? 0 : lastPk.current}&lang=${readLang()}`);
    setRoom(r.room); setMembers(r.members || []);
    const fresh2 = (r.messages || []).filter((m) => m.kind !== 'side');
    if (fresh) setMsgs(fresh2.slice(-80));
    else if (fresh2.length) setMsgs((cur) => [...cur, ...fresh2.filter((x) => !cur.some((y) => y.msg_pk === x.msg_pk))].slice(-120));
    if ((r.edits || []).length) {
      const by = new Map(r.edits.map((x) => [x.msgPk, x]));
      setMsgs((cur) => cur.map((m) => (by.has(m.msg_pk) ? { ...m, body: by.get(m.msg_pk).body, deleted: !!by.get(m.msg_pk).deleted, tr: null } : m)));
    }
    if ((r.messages || []).length) lastPk.current = Math.max(lastPk.current, ...r.messages.map((x) => x.msg_pk));
  }, [pk]);
  useEffect(() => { pull(true).catch((e) => setErr(e.message)); }, [pull]);
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) pull(false).catch(() => {}); }, min ? 8000 : 3000);
    return () => clearInterval(id);
  }, [pull, min]);
  // open and looked at: read up to the last message, as the full room does
  useEffect(() => {
    if (min || document.hidden || !lastPk.current || lastPk.current <= readPk.current) return;
    readPk.current = lastPk.current;
    call('POST', `/rooms/${pk}/read`, { msgPk: lastPk.current }).catch(() => {});
  }, [msgs, min, pk]);
  useEffect(() => { if (!min && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs.length, min]);

  const unread = (() => {
    const mine = members.find((p) => Number(p.id) === Number(me.userId));
    const last = mine ? mine.lastRead : 0;
    return msgs.filter((m) => m.msg_pk > Math.max(last, readPk.current) && Number(m.user_id) !== Number(me.userId) && (m.kind === 'user' || m.kind === 'catchy')).length;
  })();
  /* THE BOX, GOOGLE CHAT'S WAY (Max 04/11, beside its popup): "+" adds files or a photo (the full room's upload), a rounded box with
     the emoji button inside, a round Send */
  const [files, setFiles] = useState([]);   // picked, not sent yet: { key, file }
  const [emoji, setEmoji] = useState(false);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const upload = async (f) => {
    const r = await fetch(`${API}/rooms/${pk}/files?name=${encodeURIComponent(f.name || 'photo.jpg')}&type=${encodeURIComponent(f.type || '')}`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: f });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
    return j.id;
  };
  const send = async () => {
    const body = text.trim();
    if ((!body && !files.length) || busy) return;
    setBusy(true); setErr('');
    try {
      const ids = [];
      for (const f of files) ids.push(await upload(f.file)); // eslint-disable-line no-await-in-loop
      await call('POST', `/rooms/${pk}/messages`, { body, files: ids });
      setText(''); setFiles([]); if (inputRef.current) inputRef.current.style.height = ''; await pull(false);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const addEmoji = (x) => { setText((v) => `${v}${x}`); setEmoji(false); inputRef.current?.focus(); };
  const byId = new Map(members.map((p) => [p.id, p]));
  return (
    <section className={`gp-win${min ? ' min' : ''}`} style={{ right: 230 + index * 332 }} data-gp-win={pk} aria-label={room?.name || 'Group chat'}>
      <header className="gp-head" onClick={() => onFold(!min)} data-gp-head>
        <b title={room?.name}>{room?.name || '…'}</b>
        {min && unread > 0 && <span className="gp-badge" data-gp-unread>{unread}</span>}
        <span className="gp-btns" onClick={(e) => e.stopPropagation()}>
          <button type="button" title={min ? t('xGcc.popRestore') : t('xGcc.popFold')} aria-label={min ? t('xGcc.popRestore') : t('xGcc.popFold')} onClick={() => onFold(!min)} data-gp-fold><Minus size={15} /></button>
          <button type="button" title={t('xGcc.popOpen')} aria-label={t('xGcc.popOpen')} onClick={() => { onClose(); navigate(`/gcc?room=${pk}`); }} data-gp-open><Maximize2 size={14} /></button>
          <button type="button" title={t('xGcc.close')} aria-label={t('xGcc.close')} onClick={onClose} data-gp-close><X size={15} /></button>
        </span>
      </header>
      {!min && (
        <>
          <div className="gp-list" ref={listRef} data-gp-list>
            {msgs.filter((m) => m.kind !== 'side-run').map((m) => {
              if (m.kind === 'system') return <div key={m.msg_pk} className="gp-sys">{m.body}</div>;
              const own = m.kind === 'user' && Number(m.user_id) === Number(me.userId);
              const p = byId.get(m.user_id);
              const words = m.deleted ? null : (m.tr && m.tr.trim() ? m.tr : m.body);
              return (
                <div key={m.msg_pk} className={`gp-row${own ? ' me' : ''}`} data-gp-msg={m.msg_pk}>
                  {!own && (m.kind === 'catchy' ? <Mascot className="gp-bird" /> : <UserAvatar url={p?.avatar} name={p?.name || m.user_name} size={22} />)}
                  <div className="gp-col">
                    {!own && <span className="gp-who">{m.kind === 'catchy' ? 'Catchy' : (p?.name || m.user_name || '').split(' ')[0]}</span>}
                    <div className={`gp-bubble${own ? ' me' : ''}${m.kind === 'catchy' ? ' ai' : ''}`}>
                      {m.deleted ? <em>{t('xGcc.msgDeleted')}</em> : plain(words)}
                      {(m.files || []).map((f) => (f.image
                        ? <a key={f.id} href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer"><img className="gp-img" src={`${API}/files/${f.id}`} alt={f.name} /></a>
                        : <a key={f.id} className="gp-file" href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer">📎 {f.name}</a>))}
                    </div>
                    <span className="gp-time">{hhmm(m.created_at)}{m.tr && !m.deleted ? ` · ${t('xGcc.popTranslated')}` : ''}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {err && <div className="gp-err">{err}</div>}
          {files.length > 0 && (
            <div className="gp-files" data-gp-files>
              {files.map((f) => (
                <span key={f.key} className="gp-chip" title={f.file.name}><Paperclip size={11} /> {f.file.name}
                  <button type="button" onClick={() => setFiles((cur) => cur.filter((x) => x.key !== f.key))} aria-label={t('xGcc.cancel')}><X size={11} /></button></span>
              ))}
            </div>
          )}
          {emoji && (
            <div className="gp-emojis" data-gp-emojis>{EMOJI.map((x) => <button key={x} type="button" onClick={() => addEmoji(x)}>{x}</button>)}</div>
          )}
          <div className="gp-compose">
            <button type="button" className="gp-plus" title={t('xGcc.attach')} aria-label={t('xGcc.attach')} onClick={() => fileRef.current?.click()} data-gp-attach><Plus size={18} /></button>
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => { const add = [...(e.target.files || [])].slice(0, 10).map((f, i) => ({ key: `${Date.now()}-${i}`, file: f })); setFiles((cur) => [...cur, ...add].slice(0, 10)); e.target.value = ''; }} data-gp-fileinput />
            <div className="gp-pill">
              <textarea ref={inputRef} rows={1} value={text} placeholder={t('xGcc.popPh')}
                onChange={(e) => { setText(e.target.value); const el = e.target; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 110)}px`; }}
                onKeyDown={(e) => { if (enterSends(e)) { e.preventDefault(); send(); } }}
                onPaste={(e) => { const f = [...(e.clipboardData?.items || [])].filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean); if (f.length) { e.preventDefault(); setFiles((cur) => [...cur, ...f.map((x, i) => ({ key: `${Date.now()}-p${i}`, file: x }))].slice(0, 10)); } }}
                data-gp-input />
              <button type="button" className="gp-emo" title={t('xGcc.emoji')} aria-label={t('xGcc.emoji')} onClick={() => setEmoji((v) => !v)} data-gp-emoji><Smile size={18} /></button>
            </div>
            <button type="button" className="gp-send" disabled={busy || (!text.trim() && !files.length)} onClick={send} aria-label={t('xGcc.send')} title={t('xGcc.send')} data-gp-send><Send size={16} /></button>
          </div>
        </>
      )}
    </section>
  );
}

export default function GccPopups() {
  const me = useAuth();
  const location = useLocation();
  const [list, setList] = useState(load);
  const set = (fn) => setList((cur) => { const next = typeof fn === 'function' ? fn(cur) : fn; save(next); return next; });
  useEffect(() => {
    const open = (e) => {
      const pk = Number(e.detail?.pk);
      if (!pk) return;
      set((cur) => [{ pk, min: false }, ...cur.filter((x) => x.pk !== pk)].slice(0, MAX));
    };
    window.addEventListener('gcc-popup', open);
    return () => window.removeEventListener('gcc-popup', open);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (location.pathname === '/gcc' || !list.length || !me.userId) return null;
  return (
    <div className="gp-dock" data-gp-dock>
      {list.map((x, i) => (
        <Popup key={x.pk} pk={x.pk} min={x.min} index={i} me={me}
          onFold={(min) => set((cur) => cur.map((y) => (y.pk === x.pk ? { ...y, min } : y)))}
          onClose={() => set((cur) => cur.filter((y) => y.pk !== x.pk))} />
      ))}
    </div>
  );
}
